import { cn } from '@listening/utils/react-helpers';
import { clamp } from '@listening/utils/ts-utils';
import PlaybackSpeedIcon from '@listening/assets/icons/playbackSpeed.svg?react';
import PremiumLockIcon from '@listening/assets/icons/premium-lock.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import { DropdownMenuContent } from '@listening/frontend/components/radix/dropdown-menu';
import { menuSurface } from '@/components/web-fe/design-v2/surfaces';
import { Separator } from '@listening/frontend/components/radix/separator';
import { Slider } from '@listening/frontend/components/radix/slider';
import { Toggle } from '@listening/frontend/components/radix/toggle';
import {
  SPEED_PRESET_BUTTONS,
  speedRateKey,
} from '@listening/frontend/play-back-speed-presets';
import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import { useDraft } from '@listening/frontend/hooks/use-draft';
import { useCallback } from 'react';
import { useAudioChunkPlayer } from '@/lib/audio-chunk-player';
import { useAnalytics } from '@listening/frontend/analytics/client';
import * as m from '@/paraglide/messages';

const MIN_RATE = 0.25;
const MAX_RATE = 4;

/** Ceiling for an invalid subscription. Shared so every surface that can
 * write `playbackRate` enforces the same limit. */
export const FREE_SPEED_LIMIT = 1.25;
const FREE_LIMIT = FREE_SPEED_LIMIT;

function convertValueToPercentage(value: number, min: number, max: number) {
  const maxSteps = max - min;
  const percentPerStep = 100 / maxSteps;
  const percentage = percentPerStep * (value - min);
  return clamp(percentage, 0, 100);
}

type PlaybackSpeedDropdownProps = {
  isSubscriptionInvalid: boolean;
  setPlaybackSpeedPremiumDialog: () => void;
};

export function PlaybackSpeedDropdown(props: PlaybackSpeedDropdownProps) {
  const { isSubscriptionInvalid, setPlaybackSpeedPremiumDialog } = props;
  const { capture } = useAnalytics();
  const savePlaybackRate = useAudioPrefsStore((state) => state.setPlaybackRate);
  const playbackRate = useAudioPrefsStore((state) => state.playbackRate);
  const setPlayerRate = useAudioChunkPlayer().player.setRate;

  const setAndSavePlaybackRate = useCallback(
    (paramRate: number) => {
      let rate = paramRate;
      if (rate < MIN_RATE) rate = MIN_RATE;
      if (rate > MAX_RATE) rate = MAX_RATE;
      if (rate > FREE_LIMIT && isSubscriptionInvalid) {
        setPlaybackSpeedPremiumDialog();
        return;
      }
      savePlaybackRate(rate);
      setPlayerRate(rate);
      capture('set_playback_speed', { rate });
    },
    [
      isSubscriptionInvalid,
      savePlaybackRate,
      setPlaybackSpeedPremiumDialog,
      setPlayerRate,
      capture,
    ],
  );

  const [draggingPlaybackRateValue, setDraggingPlaybackRateValue] =
    useDraft(playbackRate);

  return (
    <DropdownMenuContent className={cn('flex flex-col', menuSurface)}>
      <div className='flex gap-4 px-5 py-6'>
        <PlaybackSpeedIcon className='text-[var(--v2-text-primary)]' />
        <span className='text-base font-semibold text-[var(--v2-text-primary)]'>
          Speed
        </span>
      </div>
      <Separator />
      <div className='flex flex-col gap-4 px-5 py-6'>
        <div className='flex gap-2'>
          <Button
            className='size-8 rounded-full'
            onClick={() => {
              setAndSavePlaybackRate(playbackRate - 0.05);
            }}
            variant='ghost'
          >
            <span className='text-2xl'>-</span>
          </Button>
          <div className='relative flex w-full items-center'>
            <Slider
              max={MAX_RATE}
              min={MIN_RATE}
              onValueChange={(val) => {
                const value = val[0];
                if (value === undefined) return;
                if (value > FREE_LIMIT && isSubscriptionInvalid) return;
                setDraggingPlaybackRateValue(value);
              }}
              onValueCommit={(val) => {
                const value = val[0];
                if (value === undefined) return;
                setAndSavePlaybackRate(value);
              }}
              rangeClassName='rounded-l-full'
              step={0.05}
              trackChildren={
                isSubscriptionInvalid && (
                  <div
                    className='absolute h-full rounded-full bg-gold-375/20'
                    style={{
                      right: '0px',
                      left: `${convertValueToPercentage(
                        1.5,
                        MIN_RATE,
                        MAX_RATE,
                      ).toString()}%`,
                    }}
                  >
                    <PremiumLockIcon
                      className='pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 transform'
                      style={{
                        left: '0%',
                      }}
                    />
                  </div>
                )
              }
              trackClassName='overflow-visible'
              value={[draggingPlaybackRateValue]}
            />
          </div>
          <Button
            className='size-8 rounded-full'
            onClick={() => {
              setAndSavePlaybackRate(playbackRate + 0.05);
            }}
            variant='ghost'
          >
            <span className='text-2xl'>+</span>
          </Button>
        </div>
        <div className='flex flex-col gap-3'>
          <div className='flex'>
            {SPEED_PRESET_BUTTONS.slice(0, 5).map((button) => (
              <ToggleSpeedButton
                key={button.val}
                {...button}
                setAndSavePlaybackRate={setAndSavePlaybackRate}
              />
            ))}
          </div>
          <div
            className={cn(
              'flex items-center rounded-lg',
              isSubscriptionInvalid && 'bg-gold-500/10',
            )}
          >
            {SPEED_PRESET_BUTTONS.slice(5).map((button) => (
              <ToggleSpeedButton
                key={button.val}
                {...button}
                setAndSavePlaybackRate={setAndSavePlaybackRate}
              />
            ))}
            {isSubscriptionInvalid && (
              <div className='flex w-16 items-center justify-center'>
                <div className='flex items-center justify-center rounded-full bg-gold-375/10 p-[6px]'>
                  <div className='flex items-center justify-center rounded-full bg-gold-375/15 p-[6px]'>
                    <PremiumLockIcon />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {isSubscriptionInvalid && (
          <Button
            onClick={() => {
              setPlaybackSpeedPremiumDialog();
            }}
          >
            {m.dialog_premium_speed()}
          </Button>
        )}
      </div>
    </DropdownMenuContent>
  );
}

function ToggleSpeedButton({
  val,
  precision,
  setAndSavePlaybackRate,
}: {
  val: number;
  precision?: number;
  setAndSavePlaybackRate: (rate: number) => void;
}) {
  const playbackRate = useAudioPrefsStore((state) => state.playbackRate);

  return (
    <Toggle
      className='w-16'
      onClick={() => {
        setAndSavePlaybackRate(val);
      }}
      pressed={speedRateKey(playbackRate) === speedRateKey(val)}
    >
      {val.toPrecision(precision ?? 2)}
    </Toggle>
  );
}
